import { authenticatedFetch } from '../utils/api';
import { FolderService } from './FolderService';

export interface ListInfo {
  filename: string;
  name: string; // filename without .txt extension
}

/**
 * Service to manage book lists (filter files)
 */
export class ListService {
  /**
   * Fetch all available list files from the lists folder
   */
  static async getAvailableLists(): Promise<ListInfo[]> {
    try {
      const folder = FolderService.getFolder();
      if (!folder) {
        console.warn('[ListService] No folder selected, cannot fetch lists');
        return [];
      }

      const url = FolderService.addFolderToUrl('/api/books/lists');
      const response = await authenticatedFetch(url);
      
      if (!response.ok) {
        if (response.status === 404) {
          // Lists folder doesn't exist - that's okay
          return [];
        }
        throw new Error(`Failed to fetch lists: ${response.statusText}`);
      }

      const data = await response.json();
      const lists: ListInfo[] = (data.lists || []).map((filename: string) => ({
        filename,
        name: filename.replace(/\.txt$/, ''),
      }));

      return lists;
    } catch (error) {
      console.error('[ListService] Error fetching lists:', error);
      return [];
    }
  }

  /**
   * Fetch the content of a list file (ISBNs, one per line)
   */
  static async getListContent(filename: string): Promise<string[]> {
    try {
      const folder = FolderService.getFolder();
      if (!folder) {
        throw new Error('No folder selected');
      }

      const url = FolderService.addFolderToUrl(`/api/books/lists/${filename}`);
      const response = await authenticatedFetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch list content: ${response.statusText}`);
      }

      const data = await response.json();
      const content = data.content || '';
      
      // Parse ISBNs (one per line, trim whitespace, filter empty lines)
      const isbns = content
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

      return isbns;
    } catch (error) {
      console.error(`[ListService] Error fetching list content for ${filename}:`, error);
      throw error;
    }
  }
}

